import { useEffect, useState } from "react";

/**
 * Tiny replacement for SWR / RTK Query — enough for Phase 3's read-only screens.
 * Phase 4+ migrate to RTK Query for caching / invalidation / mutations.
 *
 * Returns ``{ data, error, loading }``. ``deps`` re-fetches on change.
 */
export function useApiQuery<T>(
  fetcher: () => Promise<T>,
  deps: unknown[],
): { data: T | null; error: string | null; loading: boolean } {
  const [data,    setData]    = useState<T | null>(null);
  const [error,   setError]   = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcher()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = (e as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail
          ?? (e as Error)?.message
          ?? "Request failed";
        setError(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, error, loading };
}
